'use client';

import { useRouter } from 'next/navigation';
import { useGoBack } from '@/hooks/use-go-back';
import { Separator, Skeleton, toast } from '@mokoboko/components';
import { PageContainer } from './page-container';
import {
  DetailBuyButton,
  DetailCard,
  DetailTopBar,
  DETAIL_HERO,
  DETAIL_HERO_MEDIA,
  DETAIL_HERO_TEXT,
} from './detail-card';
import { AppearanceSettings } from '@/state/appearance-settings';
import { mergeClassNames } from '@/lib/utils';
import { ClassPack } from '@/state/class-pack-groups';
import { CartData } from '@/lib/types';
import { trackMetaConversion } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { goToCheckout } from '@/helpers/checkout-navigation';
import { useAuth } from '@/contexts/auth';
import useCustomerHistory from '@/state/customer-history';
import usePurchaseEligibility from '@/state/purchase-eligibility';
import useTenant from '@/state/tenant';
import { ImageGallery, GALLERY_SKELETON } from './image-gallery';
import { getSortedImages } from '@/lib/entity-image';
import { DESCRIPTION_PROSE } from '@/lib/prose';
import { getClassPackFacts } from '@/helpers/offering-facts';
import {
  OfferingFactTiles,
  OfferingHeroFacts,
  OfferingHeroFactsSkeleton,
} from './offering-facts';
import { OfferingDetailSections } from './offering-detail-sections';
import { BOUGHT_BEFORE } from './restriction-alert';

export const ClassPackDetails = ({
  classPack,
  classPackSettings,
}: {
  classPack: ClassPack;
  classPackSettings: AppearanceSettings['class_pack'];
}) => {
  const router = useRouter();
  const { goBack, hasBack } = useGoBack();
  const { user } = useAuth();
  const shouldCheckNewCustomer = !!user && !!classPack.new_customers_only;
  const { isNewCustomer } = useCustomerHistory({
    userId: shouldCheckNewCustomer ? user!.user_id : undefined,
  });
  const showNewCustomerWarning = shouldCheckNewCustomer && !isNewCustomer;

  const { classPackEligibility } = usePurchaseEligibility({
    userId: user?.user_id,
    classPackId: user ? classPack.class_pack_id : undefined,
  });
  const showAlreadyPurchasedWarning =
    classPackEligibility?.reason === 'ALREADY_PURCHASED';

  const restrictionReason = showNewCustomerWarning
    ? 'This class pack is available for new customers only'
    : showAlreadyPurchasedWarning
      ? BOUGHT_BEFORE['class-pack'].reason
      : undefined;
  const restrictionDetail =
    !showNewCustomerWarning && showAlreadyPurchasedWarning
      ? BOUGHT_BEFORE['class-pack'].detail
      : null;

  const descriptionText = classPack.description ?? classPack.short_description;

  const sortedImages = classPack.images
    ? getSortedImages(classPack.images)
    : [];
  const showImage = classPackSettings.show_image;
  const { data: tenant } = useTenant();
  const facts = getClassPackFacts(classPack, classPackSettings, tenant?.name);
  const hasImages = sortedImages.length > 0 || !!classPack.image;

  const handleCheckout = () => {
    const cartData: CartData<'class-pack'> = {
      items: [
        {
          ...classPack,
          quantity: 1,
          price: classPack.price ?? 0,
        },
      ],
      totalAmount: classPack.price ?? 0,
      checkoutType: 'class-pack',
    };

    if (!goToCheckout(router, cartData)) {
      toast.info('Allow pop-ups to continue to checkout.');
      return;
    }

    trackMetaConversion({
      event: 'AddToCart',
      custom_data: {
        content_name: classPack.name,
        content_type: 'class_pack',
        content_ids: [classPack.slug],
        value: classPack.price ?? 0,
        currency: classPack.currency,
      },
    });
    track({
      event: AnalyticsEvent.PRODUCT_ADDED_TO_CART,
      properties: {
        product_type: 'class_pack',
        product_name: classPack.name,
        product_id: classPack.class_pack_id,
        price: classPack.price,
        currency: classPack.currency ?? null,
        quantity: 1,
      },
    });
  };

  const buyButton = {
    label: 'Buy pack',
    reason: restrictionReason,
    reasonDetail: restrictionDetail,
    onClick: handleCheckout,
    reasonAction: {
      label: 'Select another',
      onClick: () => router.push('/packages'),
    },
  };

  return (
    <PageContainer>
      <DetailCard hasBack={hasBack}>
        <DetailTopBar
          hasBack={hasBack}
          onBack={() => goBack('/packages')}
          buyButton={buyButton}
        />

        <div className={DETAIL_HERO}>
          <div
            className={mergeClassNames(
              DETAIL_HERO_TEXT,
              hasImages && showImage ? 'order-2 md:order-1' : ''
            )}
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="flex flex-1 flex-col gap-5 sm:gap-2">
                <h1 className="heading-page">{classPack.name}</h1>
                <OfferingHeroFacts facts={facts} />
              </div>
              {!hasBack && <DetailBuyButton {...buyButton} />}
            </div>

            <DetailBuyButton {...buyButton} phone />

            {classPackSettings.show_description && descriptionText && (
              <div
                className={DESCRIPTION_PROSE}
                dangerouslySetInnerHTML={{ __html: descriptionText }}
              />
            )}
          </div>

          {hasImages && showImage && (
            <div className={DETAIL_HERO_MEDIA}>
              <ImageGallery
                images={sortedImages}
                alt={classPack.name}
                fallbackUrl={classPack.image}
              />
            </div>
          )}
        </div>

        <OfferingFactTiles facts={facts} />

        <OfferingDetailSections
          settings={classPackSettings}
          classIds={classPack.class_pack_classes?.map((c) => c.class_id) ?? []}
          productType="class-pack"
          slug={classPack.slug}
        />
      </DetailCard>
    </PageContainer>
  );
};

export const ClassPackDetailsSkeleton = () => {
  const { hasBack } = useGoBack();

  return (
    <PageContainer>
      <DetailCard hasBack={hasBack}>
        {hasBack && (
          <>
            <div className="flex items-center justify-between">
              <Skeleton
                isRounded
                className="!h-11 w-[124px] sm:!h-10 sm:w-[108px]"
              />
              <Skeleton
                isRounded
                className="hidden !h-10 sm:block sm:w-[100px]"
              />
            </div>
            <Separator />
          </>
        )}

        <div className={DETAIL_HERO}>
          <div
            className={mergeClassNames(DETAIL_HERO_TEXT, 'order-2 md:order-1')}
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="flex flex-1 flex-col gap-5 sm:gap-2">
                <Skeleton text="h2" width={208} />
                <OfferingHeroFactsSkeleton />
              </div>
              {!hasBack && (
                <Skeleton
                  isRounded
                  className="hidden !h-10 shrink-0 sm:block sm:w-[100px]"
                />
              )}
            </div>

            <div className="sm:hidden">
              <Skeleton isRounded height={44} width="100%" />
            </div>

            <div className="flex flex-col gap-2">
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" className="w-2/3" />
            </div>
          </div>

          <div className={DETAIL_HERO_MEDIA}>
            <Skeleton isRounded className={GALLERY_SKELETON} />
          </div>
        </div>
      </DetailCard>
    </PageContainer>
  );
};
